import React, { useMemo } from 'react';
import { Box, Typography, IconButton, Button, Chip } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import dayjs, { Dayjs } from 'dayjs';
import { UpcomingEventResponse } from '../../../types/events';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MAX_VISIBLE = 3;

function isBirthdayEvent(event: UpcomingEventResponse): boolean {
  return (
    event.event_type_id === 'birthday' ||
    event.event_type_name === 'Birthday' ||
    event.id.startsWith('birthday-')
  );
}

function eventDayKey(meetingOn: string): string {
  return dayjs(meetingOn).format('YYYY-MM-DD');
}

export interface EventsMonthCalendarProps {
  month: Dayjs;
  events: UpcomingEventResponse[];
  canEditEvents: boolean;
  onMonthChange: (month: Dayjs) => void;
  onEventClick: (eventId: string) => void;
  onEmptyDayClick: (day: Dayjs) => void;
}

const EventsMonthCalendar: React.FC<EventsMonthCalendarProps> = ({
  month,
  events,
  canEditEvents,
  onMonthChange,
  onEventClick,
  onEmptyDayClick,
}) => {
  const monthStart = month.startOf('month');
  const monthEnd = month.endOf('month');
  const gridStart = monthStart.startOf('week');
  const gridEnd = monthEnd.endOf('week');

  const days = useMemo(() => {
    const out: Dayjs[] = [];
    let d = gridStart;
    while (d.isBefore(gridEnd) || d.isSame(gridEnd, 'day')) {
      out.push(d);
      d = d.add(1, 'day');
    }
    return out;
  }, [gridStart, gridEnd]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, UpcomingEventResponse[]>();
    for (const ev of events) {
      if (!ev.meeting_on) continue;
      const key = eventDayKey(ev.meeting_on);
      const list = map.get(key) || [];
      list.push(ev);
      map.set(key, list);
    }
    return map;
  }, [events]);

  const today = dayjs().startOf('day');

  return (
    <Box sx={{ width: '100%', p: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton
            aria-label="Previous month"
            onClick={() => onMonthChange(month.subtract(1, 'month'))}
            size="small"
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="h6" sx={{ minWidth: 160, textAlign: 'center' }}>
            {month.format('MMMM YYYY')}
          </Typography>
          <IconButton
            aria-label="Next month"
            onClick={() => onMonthChange(month.add(1, 'month'))}
            size="small"
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
        <Button
          size="small"
          variant="outlined"
          onClick={() => onMonthChange(dayjs().startOf('month'))}
        >
          Today
        </Button>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 0.5,
          mb: 0.5,
        }}
      >
        {WEEKDAYS.map((label) => (
          <Typography
            key={label}
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: 'center', fontWeight: 600 }}
          >
            {label}
          </Typography>
        ))}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 0.5,
        }}
      >
        {days.map((day) => {
          const key = day.format('YYYY-MM-DD');
          const inMonth = day.month() === month.month();
          const isToday = day.isSame(today, 'day');
          const dayEvents = eventsByDay.get(key) || [];
          const visible = dayEvents.slice(0, MAX_VISIBLE);
          const overflow = dayEvents.length - visible.length;

          return (
            <Box
              key={key}
              onClick={() => {
                if (canEditEvents) onEmptyDayClick(day);
              }}
              sx={{
                minHeight: 110,
                border: '1px solid',
                borderColor: isToday ? 'primary.main' : 'divider',
                borderRadius: 1,
                p: 0.5,
                bgcolor: inMonth ? 'background.paper' : 'action.hover',
                opacity: inMonth ? 1 : 0.65,
                cursor: canEditEvents ? 'pointer' : 'default',
                '&:hover': canEditEvents
                  ? { bgcolor: inMonth ? 'action.selected' : 'action.hover' }
                  : undefined,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: isToday ? 700 : 500,
                  color: isToday ? 'primary.main' : 'text.primary',
                  display: 'block',
                  mb: 0.5,
                }}
              >
                {day.date()}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                {visible.map((ev) => {
                  const birthday = isBirthdayEvent(ev);
                  return (
                    <Chip
                      key={ev.id}
                      size="small"
                      label={ev.title}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!birthday) onEventClick(ev.id);
                      }}
                      sx={{
                        height: 'auto',
                        minHeight: 22,
                        justifyContent: 'flex-start',
                        borderRadius: 0.5,
                        '& .MuiChip-label': {
                          display: 'block',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          px: 0.5,
                          py: 0.25,
                          fontSize: 11,
                        },
                        bgcolor: birthday ? 'secondary.light' : 'primary.light',
                        color: birthday
                          ? 'secondary.contrastText'
                          : 'primary.contrastText',
                        cursor: birthday ? 'default' : 'pointer',
                      }}
                    />
                  );
                })}
                {overflow > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    +{overflow} more
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default EventsMonthCalendar;

/** Inclusive calendar grid range for a month (Sun–Sat weeks). */
export function calendarGridRange(month: Dayjs): { start: Dayjs; end: Dayjs } {
  const monthStart = month.startOf('month');
  const monthEnd = month.endOf('month');
  return {
    start: monthStart.startOf('week').startOf('day'),
    end: monthEnd.endOf('week').startOf('day'),
  };
}
